import { rtdb, auth } from "../firebase";
import { ref, get, set, onValue, runTransaction } from "firebase/database";

// Map DOCX filenames to RTDB keys used in the existing project
const toRtdbKey = (fileName) => {
  if (!fileName) return null;
  const m = fileName.match(/form(\d+)/i);
  if (m) return `form${m[1].toLowerCase()}`.toLowerCase();
  // fallback: strip non-alnum and lower
  return fileName.replace(/[^a-z0-9]/gi, "").toLowerCase();
};

const RTDB_PATH = "/counts";

/**
 * Read all counts from Realtime Database (one-time read)
 * Returns object like { "FORM1.docx": 12, "FORM3.docx": 6, ... }
 */
export async function fetchCounts() {
  try {
    const snap = await get(ref(rtdb, RTDB_PATH));
    const raw = snap.exists() ? snap.val() : {};
    const result = {};
    // raw is expected to be { form1: { count: n }, form3: { count: n } }
    for (const [key, val] of Object.entries(raw || {})) {
      const count = val && typeof val === "object" ? val.count || 0 : val || 0;
      // map back to DOCX name: FORM{N}.docx
      const m = key.match(/^form(\d+)/i);
      if (m) {
        result[`FORM${m[1]}.docx`] = Number(count);
      } else {
        // fallback use key directly
        result[key] = Number(count);
      }
    }
    // ensure keys exist
    ["FORM1.docx", "FORM3.docx", "FORM5.docx"].forEach((f) => {
      if (result[f] == null) result[f] = 0;
    });
    return result;
  } catch (err) {
    console.warn("Could not fetch counts from RTDB:", err);
    return { "FORM1.docx": 0, "FORM3.docx": 0, "FORM5.docx": 0 };
  }
}

/**
 * Subscribe to Realtime Database counts. Callback receives same shaped object as fetchCounts
 * Returns unsubscribe function
 */
export function subscribeToCountsRealtime(callback) {
  const countsRef = ref(rtdb, RTDB_PATH);
  const listener = onValue(countsRef, (snap) => {
    const raw = snap.exists() ? snap.val() : {};
    const mapped = {};
    for (const [key, val] of Object.entries(raw || {})) {
      const count = val && typeof val === "object" ? val.count || 0 : val || 0;
      const m = key.match(/^form(\d+)/i);
      if (m) mapped[`FORM${m[1]}.docx`] = Number(count);
      else mapped[key] = Number(count);
    }
    ["FORM1.docx", "FORM3.docx", "FORM5.docx"].forEach((f) => {
      if (mapped[f] == null) mapped[f] = 0;
    });
    callback(mapped);
  }, (err) => {
    console.error("RTDB onValue error:", err);
    callback({ "FORM1.docx": 0, "FORM3.docx": 0, "FORM5.docx": 0 });
  });
  // onValue returns an unsubscribe function when you call off(ref, 'value', listener)
  // but in modular SDK the onValue itself returns an unsubscribe function
  return listener;
}

/**
 * Atomically increment count for a given file (maps to RTDB key)
 * Returns new numeric count
 */
export async function incrementCount(fileName) {
  try {
    // require user to be signed in (RTDB rules expect auth)
    if (!auth || !auth.currentUser) {
      throw new Error("auth_required: user must be signed in to increment counts");
    }
    const key = toRtdbKey(fileName);
    if (!key) throw new Error("Invalid file name for increment");
    const nodeRef = ref(rtdb, `${RTDB_PATH}/${key}/count`);
    const result = await runTransaction(nodeRef, (current) => {
      return (current || 0) + 1;
    });
    // result.snapshot.val() should contain the new count
    const newCount = result.snapshot && result.snapshot.val ? result.snapshot.val() : null;
    return Number(newCount);
  } catch (err) {
    // improve permission error message for easier debugging
    console.error("Error incrementing RTDB count:", err);
    if (err && err.message && err.message.toLowerCase().includes("permission_denied")) {
      throw new Error(
        "permission_denied: Realtime Database rules prevented the write. Ensure the RTDB rules allow authenticated users to write to /counts and that the user is signed in."
      );
    }
    if (err && err.message && err.message.toLowerCase().includes("auth_required")) {
      throw err;
    }
    throw err;
  }
}
